import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type StepStatus =
  | "pending"
  | "in_progress"
  | "blocked"
  | "complete"
  | "not_applicable";

export type PeriodStatus = "not_started" | "in_progress" | "signed_off";

export type ReconRole =
  | "cs"
  | "revops"
  | "cro"
  | "finance"
  | "partner"
  | "controller"
  | "budget_owner"
  | "approver";

export interface StepEvent {
  action: "completed" | "unlocked";
  note: string | null;
  actor_name: string | null;
  at: string;
}

export interface StepState {
  step_key: string;
  order: number;
  title: string;
  mode: string;
  owner_roles: ReconRole[];
  status: StepStatus;
  notes: string | null;
  data: Record<string, unknown>;
  completed_by: string | null;
  completed_by_name: string | null;
  completed_at: string | null;
  /** complete == locked: no edits until an admin unlocks (with a note). */
  locked: boolean;
  /** Whether the current user may act on this step (per-owner-role gating). */
  can_act: boolean;
  /** Whether the current user may unlock a completed step (admin). */
  can_unlock: boolean;
  /** Lock-lifecycle audit, oldest first: completions + unlocks with notes. */
  events: StepEvent[];
}

export interface PeriodSummary {
  period: string; // YYYY-MM
  status: PeriodStatus;
  exists: boolean;
  steps_total: number;
  steps_done: number;
  signed_off_at: string | null;
  signed_off_by: string | null;
  signed_off_by_name: string | null;
}

export interface PeriodDetail extends PeriodSummary {
  notes: string | null;
  can_open: boolean;
  can_sign_off: boolean;
  steps: StepState[];
}

export interface StepUpdate {
  status?: StepStatus;
  notes?: string;
  data?: Record<string, unknown>;
}

const KEY = "reconciliation";

export function useReconPeriods() {
  return useQuery({
    queryKey: [KEY, "periods"],
    queryFn: () => apiFetch<PeriodSummary[]>("/reconciliation/periods"),
    staleTime: 60_000,
  });
}

export function useReconPeriod(period: string) {
  return useQuery({
    queryKey: [KEY, "period", period],
    queryFn: () => apiFetch<PeriodDetail>(`/reconciliation/periods/${period}`),
    enabled: !!period,
    staleTime: 60_000,
  });
}

export function useOpenPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodDetail>(`/reconciliation/periods/${period}/open`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useUpdateStep(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { stepKey: string; update: StepUpdate }) =>
      apiFetch<StepState>(
        `/reconciliation/periods/${period}/steps/${vars.stepKey}`,
        { method: "PATCH", body: vars.update },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useSignOff(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes?: string) =>
      apiFetch<PeriodDetail>(`/reconciliation/periods/${period}/sign-off`, {
        method: "POST",
        body: { notes: notes ?? null },
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useUnlockStep(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { stepKey: string; note: string }) =>
      apiFetch<StepState>(
        `/reconciliation/periods/${period}/steps/${vars.stepKey}/unlock`,
        { method: "POST", body: { note: vars.note } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
      // Unlocking un-freezes the step's worklist views too.
      void qc.invalidateQueries({ queryKey: [KEY, "billed-actuals", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "billing-start", period] });
    },
  });
}

export function useReopen(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodDetail>(`/reconciliation/periods/${period}/reopen`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

// ── R-01 billed actuals ──────────────────────────────────────────────────

export type ReviewStatus = "pending" | "approved" | "flagged";

/** One QB invoice / credit-memo sales line as it appeared on the document
 *  (credit-memo amounts arrive negated). */
export interface BilledLine {
  source: "invoice" | "credit_memo";
  doc_number: string | null;
  txn_date: string | null;
  item_id: string | null;
  item_name: string | null;
  description: string | null;
  quantity: number | null;
  unit_price: number | null;
  amount: number;
  /** Waterfall MRR flag for the item; null = item never made the waterfall. */
  is_mrr: boolean | null;
}

export interface BilledAccount {
  qb_id: number;
  qb_customer_name: string | null;
  cinch_account_name: string | null;
  billed_prior: number;
  billed_current: number;
  mrr_current: number;
  /** Raw-QB cross-check (invoices − credit memos); null = no QB docs. */
  qb_invoice_total: number | null;
  qb_doc_count: number | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  lines: BilledLine[];
}

export interface BilledActualsSummary {
  account_count: number;
  billed_prior_total: number;
  billed_current_total: number;
  mrr_total: number;
  qb_total: number;
  mismatch_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface BilledActualsPayload {
  period: string;
  prior_period: string;
  pulled_at: string | null;
  pulled_by_name: string | null;
  can_act: boolean;
  /** R-00: the controller has attested invoicing is final. Gates the pull. */
  invoicing_confirmed: boolean;
  summary: BilledActualsSummary | null;
  accounts: BilledAccount[];
}

export interface BilledAccountReview {
  status: ReviewStatus;
  /** Omit to leave notes untouched; "" clears them. */
  notes?: string;
}

export function useBilledActuals(period: string) {
  return useQuery({
    queryKey: [KEY, "billed-actuals", period],
    queryFn: () =>
      apiFetch<BilledActualsPayload>(
        `/reconciliation/periods/${period}/billed-actuals`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

export function usePullBilledActuals(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<BilledActualsPayload>(
        `/reconciliation/periods/${period}/billed-actuals/pull`,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "billed-actuals", period], data);
      // The pull nudges the step/period status forward too.
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useReviewBilledAccount(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { qbId: number; review: BilledAccountReview }) =>
      apiFetch<BilledAccount>(
        `/reconciliation/periods/${period}/billed-actuals/${vars.qbId}/review`,
        { method: "PATCH", body: vars.review },
      ),
    onSuccess: (account) => {
      // Patch the account in place — a full refetch would re-send every line.
      qc.setQueryData(
        [KEY, "billed-actuals", period],
        (prev: BilledActualsPayload | undefined) =>
          prev
            ? {
                ...prev,
                accounts: prev.accounts.map((a) =>
                  a.qb_id === account.qb_id ? account : a,
                ),
              }
            : prev,
      );
    },
  });
}

// ── R-14 all signed contracts in ─────────────────────────────────────────

/** One contract read LIVE from Cinch, carrying its stored verdict. The
 *  contract's full record is fetched separately per-row by the drawer. */
export interface SignedContract {
  id: string;
  company_name: string | null;
  company_dba: string | null;
  contract_type_name: string | null;
  mrr_total: number | null;
  term_months: number | null;
  payment_type: string | null;
  sign_date: string | null;
  billing_effective_from: string | null;
  closer_name: string | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  /** A verdict existed but the contract changed under it, so it lapsed. The
   *  row reads as pending and the UI says why. */
  review_superseded: boolean;
}

export interface SignedContractsSummary {
  contract_count: number;
  mrr_total: number;
  missing_effective_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface SignedContractsPayload {
  period: string;
  can_act: boolean;
  locked: boolean;
  /** When the audit copy was written — set on completion, not on read. */
  frozen_at: string | null;
  contract_count: number;
  mrr_total: number;
  arr_total: number;
  summary: SignedContractsSummary | null;
  contracts: SignedContract[];
  source: string;
  /** Set when Cinch couldn't be reached; the page renders empty and says why. */
  error: string | null;
}

/** A contract this one is linked to — Cinch's ContractSimpleSerializer. */
export interface SignedContractRef {
  id: string;
  company_name?: string | null;
  sign_date?: string | null;
  mrr_total?: number | null;
  contract_type_name?: string | null;
  billing_effective_from?: string | null;
}

/** One contract's full record, LIVE from Cinch. Deliberately loose: this is
 *  Cinch's detail serializer passed through, and the drawer reads a documented
 *  subset — everything someone validating the contract against its PandaDoc
 *  would need to check. */
export interface SignedContractDetail {
  id: string;
  base_fee?: number | null;
  pandadoc_id?: string | null;
  hubspot_deal_id?: string | null;
  stripe_customer_id?: string | null;
  mrr_total?: number | null;
  sign_date?: string | null;
  billing_effective_from?: string | null;
  billing_effective_through?: string | null;
  renewal_date?: string | null;
  next_renewal_when?: string | null;
  contract_type_name?: string | null;
  contract_status?: string | null;
  company_name?: string | null;
  company_dba?: string | null;
  account_name?: string | null;
  assigned_company_name?: string | null;
  term_months?: number | null;
  payment_type?: string | null;
  closer_name?: string | null;
  lead_source?: string | null;
  notes?: string | null;
  additional_terms?: string | null;
  // Renewal shape.
  is_auto_renew?: boolean | null;
  renewal_term?: string | null;
  renewal_term_months?: number | null;
  mtm_uplift_pct?: number | null;
  notice_period_days?: number | null;
  // Debook lifecycle — a debooked contract on the signed list is a finding.
  debooked?: boolean | null;
  debooked_reason?: string | null;
  // What they contracted for, which is what the line items should add up to.
  initial_contacts?: number | null;
  ten_k_more?: number | null;
  use_email_count?: boolean | null;
  custom_terms?: boolean | null;
  // Who signed it — checked against the PandaDoc.
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** Cinch's contract line shape. A line references an item XOR a package, so
   *  the name comes from whichever `*_detail` is populated. Mirrors
   *  `contracts/api.ts` ContractLineItem — do not guess these names. */
  contract_line_items?: {
    uuid?: string;
    item?: string | null;
    package?: string | null;
    // Cinch serialises decimals as STRINGS ("70.000000"). Typed honestly here
    // so callers coerce rather than calling number methods on a string.
    qty?: number | string | null;
    price_per_unit?: number | string | null;
    overage_price_per_unit?: number | string | null;
    item_total?: number | string | null;
    item_detail?: { name?: string | null } | null;
    package_detail?: { name?: string | null } | null;
  }[];
  /** Discounts carry no name — the type + value IS the description. */
  contract_discounts?: {
    discount_detail?: {
      type?: string | null;
      value?: number | null;
      duration_periods?: number | null;
    } | null;
    duration_override?: number | null;
    /** null = whole-contract; otherwise the line item's uuid. */
    target_line_item?: string | null;
  }[];
  // Lineage — the reason a "new" contract might not be new revenue.
  replaced_by_contracts?: SignedContractRef[];
  replacement_contract_details?: SignedContractRef | null;
  amended_by_contracts?: SignedContractRef[];
  amendment_contract_details?: SignedContractRef | null;
  reseller_by_contracts?: SignedContractRef[];
  reseller_contract_details?: SignedContractRef | null;
}

export function useSignedContracts(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "signed-contracts", period],
    queryFn: () =>
      apiFetch<SignedContractsPayload>(
        `/reconciliation/periods/${period}/signed-contracts`,
      ),
    enabled: enabled && !!period,
    staleTime: 60_000,
  });
}

function useInvalidateSignedContracts(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "signed-contracts", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

export function useReviewSignedContract(period: string) {
  const invalidate = useInvalidateSignedContracts(period);
  return useMutation({
    mutationFn: (vars: {
      contractId: string;
      status: ReviewStatus;
      notes?: string | null;
    }) =>
      apiFetch<SignedContract>(
        `/reconciliation/periods/${period}/signed-contracts/${vars.contractId}/review`,
        { method: "PATCH", body: { status: vars.status, notes: vars.notes } },
      ),
    onSuccess: invalidate,
  });
}

/** Live contract detail for the drawer. Only fetched once a contract is opened
 *  — a per-row Cinch call for every row would be gratuitous. */
export function useSignedContractDetail(period: string, contractId: string | null) {
  return useQuery({
    queryKey: [KEY, "signed-contract-detail", contractId],
    queryFn: () =>
      apiFetch<SignedContractDetail>(
        `/reconciliation/periods/${period}/signed-contracts/${contractId}`,
      ),
    enabled: !!period && !!contractId,
    staleTime: 30_000,
  });
}

// ── R-02 set billing start ───────────────────────────────────────────────

export type BillingStartKind = "first_billed" | "missing_effective";

/** Snapshot payload for a first_billed row (missing_effective rows carry the
 *  contract fields instead — both are frozen at pull time). */
export interface BillingStartItem {
  item_key: string;
  kind: BillingStartKind;
  payload: {
    qb_id?: number | null;
    qb_customer_name?: string | null;
    cinch_account_name?: string | null;
    contract_id?: string | null;
    contract_type_name?: string | null;
    contract_signed_at?: string | null;
    billing_effective_from?: string | null;
    mrr_total?: number | null;
    first_invoice_date?: string | null;
    first_month?: string | null;
    aligned?: boolean;
    invoice_discrepancy?: boolean;
    // missing_effective extras
    company_name?: string | null;
    company_name_dba?: string | null;
    term_months?: number | null;
    closer_name?: string | null;
  };
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface BillingStartSummary {
  first_billed_count: number;
  misaligned_count: number;
  invoice_discrepancy_count: number;
  missing_effective_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface BillingStartPayload {
  period: string;
  pulled_at: string | null;
  pulled_by_name: string | null;
  can_act: boolean;
  locked: boolean;
  summary: BillingStartSummary | null;
  items: BillingStartItem[];
}

export function useBillingStart(period: string) {
  return useQuery({
    queryKey: [KEY, "billing-start", period],
    queryFn: () =>
      apiFetch<BillingStartPayload>(
        `/reconciliation/periods/${period}/billing-start`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

export function usePullBillingStart(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<BillingStartPayload>(
        `/reconciliation/periods/${period}/billing-start/pull`,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "billing-start", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useReviewBillingStartItem(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      itemKey: string;
      review: { status: ReviewStatus; notes?: string };
    }) =>
      apiFetch<BillingStartItem>(
        `/reconciliation/periods/${period}/billing-start/${encodeURIComponent(vars.itemKey)}/review`,
        { method: "PATCH", body: vars.review },
      ),
    onSuccess: (item) => {
      qc.setQueryData(
        [KEY, "billing-start", period],
        (prev: BillingStartPayload | undefined) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((i) =>
                  i.item_key === item.item_key ? item : i,
                ),
              }
            : prev,
      );
    },
  });
}

// ── R-03 compute the gap ─────────────────────────────────────────────────

/** Why a contract is booked but not billing. */
export type GapDisposition =
  | "not_started_yet"
  | "should_have_billed"
  | "debook_candidate";

export interface GapContract {
  contract_id: string;
  company_name: string | null;
  company_dba: string | null;
  cinch_account_name: string | null;
  qb_id: number | null;
  contract_type_name: string | null;
  contract_signed_at: string | null;
  billing_effective_from: string | null;
  mrr_total: number;
  term_months: number | null;
  closer_name: string | null;
  is_onboard_complete: boolean | null;
  is_onboard_abandoned: boolean | null;
  /** Months from when billing should have started to the close month end. */
  months_overdue: number;
  disposition: GapDisposition;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

/** A standing debook — durable, not scoped to one close. */
export interface DebookedContract {
  contract_id: string;
  company_name: string | null;
  reason: string | null;
  decided_in_period: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
}

export interface GapSummary {
  contract_count: number;
  gap_mrr: number;
  gap_arr: number;
  not_started_count: number;
  should_have_billed_count: number;
  debook_candidate_count: number;
  debook_candidate_mrr: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface GapPayload {
  period: string;
  pulled_at: string | null;
  pulled_by_name: string | null;
  can_act: boolean;
  locked: boolean;
  summary: GapSummary | null;
  contracts: GapContract[];
  debooked: DebookedContract[];
}

export function useGap(period: string) {
  return useQuery({
    queryKey: [KEY, "gap", period],
    queryFn: () => apiFetch<GapPayload>(`/reconciliation/periods/${period}/gap`),
    enabled: !!period,
    staleTime: 60_000,
  });
}

export function usePullGap(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<GapPayload>(`/reconciliation/periods/${period}/gap/pull`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "gap", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useReviewGapContract(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      contractId: string;
      review: { status: ReviewStatus; notes?: string };
    }) =>
      apiFetch<GapContract>(
        `/reconciliation/periods/${period}/gap/${vars.contractId}/review`,
        { method: "PATCH", body: vars.review },
      ),
    onSuccess: (contract) => {
      qc.setQueryData([KEY, "gap", period], (prev: GapPayload | undefined) =>
        prev
          ? {
              ...prev,
              contracts: prev.contracts.map((c) =>
                c.contract_id === contract.contract_id ? contract : c,
              ),
            }
          : prev,
      );
    },
  });
}

/** Debook (reason required) or reverse it. Returns the whole payload because
 *  the contract moves between the worklist and the debooked list. */
export function useSetDebooked(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contractId: string; reason?: string }) =>
      vars.reason != null
        ? apiFetch<GapPayload>(
            `/reconciliation/periods/${period}/gap/${vars.contractId}/debook`,
            { method: "POST", body: { reason: vars.reason } },
          )
        : apiFetch<GapPayload>(
            `/reconciliation/periods/${period}/gap/${vars.contractId}/debook`,
            { method: "DELETE" },
          ),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "gap", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
    },
  });
}

// ── R-04 detect direct churn ─────────────────────────────────────────────

/** What an account that stopped billing actually is. */
export type ChurnDisposition =
  /** A churn/cancel date is on record — the dates just need settling. */
  | "confirmed"
  /** Billing stopped and nobody recorded why or when. CS backfills. */
  | "missing_notice"
  /** Folded into another account — the revenue moved, it didn't leave. */
  | "merged"
  /** Billed and credited back to zero the same month; never really stopped. */
  | "netted_to_zero";

/** The verdict a close settles on. */
export type ChurnOutcome = "churned" | "merged" | "not_churn";

export interface ChurnContract {
  contract_id: string;
  company_name: string | null;
  contract_type_name: string | null;
  mrr_total: number;
  contract_canceled_at: string | null;
  stop_billing_at: string | null;
  replaced_contract_id: string | null;
}

/** The durable, cross-period answer for one account. */
export interface SettledChurn {
  qb_id: number;
  qb_customer_name: string | null;
  cinch_account_name: string | null;
  outcome: ChurnOutcome;
  notice_date: string | null;
  end_date: string | null;
  reason: string | null;
  mrr_at_churn: number;
  date_source: string | null;
  decided_in_period: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
}

export interface ChurnAccount {
  qb_id: number;
  qb_customer_name: string | null;
  cinch_account_name: string | null;
  last_billed_month: string | null;
  months_at_zero: number;
  billed_prior: number;
  mrr_prior: number;
  disposition: ChurnDisposition;
  /** no_billing_record | billed_short | sources_disagree | no_cinch_account | no_contracts */
  flags: string[];
  contract_canceled_at: string | null;
  stop_billing_at: string | null;
  hs_notice_date: string | null;
  hs_churn_date: string | null;
  hs_cancel_reason: string | null;
  company_cancel_date: string | null;
  suggested_notice_date: string | null;
  suggested_notice_source: string | null;
  suggested_end_date: string | null;
  suggested_end_source: string | null;
  notice_to_end_days: number | null;
  contracts: ChurnContract[];
  settled: SettledChurn | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface ChurnSummary {
  account_count: number;
  churned_count: number;
  churned_mrr: number;
  churned_arr: number;
  confirmed_count: number;
  missing_notice_count: number;
  missing_notice_mrr: number;
  merged_count: number;
  merged_mrr: number;
  netted_to_zero_count: number;
  billed_short_count: number;
  no_billing_record_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface ChurnPayload {
  period: string;
  pulled_at: string | null;
  pulled_by_name: string | null;
  can_act: boolean;
  locked: boolean;
  lookback_months: number;
  summary: ChurnSummary | null;
  accounts: ChurnAccount[];
  settled: SettledChurn[];
}

export interface SettleChurnRequest {
  outcome: ChurnOutcome;
  notice_date: string | null;
  end_date: string | null;
  reason: string | null;
}

export function useChurn(period: string) {
  return useQuery({
    queryKey: [KEY, "churn", period],
    queryFn: () =>
      apiFetch<ChurnPayload>(`/reconciliation/periods/${period}/churn`),
    staleTime: 30_000,
  });
}

export function usePullChurn(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ChurnPayload>(`/reconciliation/periods/${period}/churn/pull`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "churn", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
    },
  });
}

export function useReviewChurnAccount(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      qbId: number;
      review: { status: ReviewStatus; notes?: string };
    }) =>
      apiFetch<ChurnAccount>(
        `/reconciliation/periods/${period}/churn/${vars.qbId}/review`,
        { method: "PATCH", body: vars.review },
      ),
    onSuccess: (row) => {
      qc.setQueryData([KEY, "churn", period], (prev: ChurnPayload | undefined) =>
        prev
          ? {
              ...prev,
              accounts: prev.accounts.map((a) =>
                a.qb_id === row.qb_id ? row : a,
              ),
            }
          : prev,
      );
      void qc.invalidateQueries({ queryKey: [KEY, "churn", period] });
    },
  });
}

/**
 * Settle (or reverse) an account's churn dates. The decision is DURABLE — it
 * stands across closes — and it's what the cash forecast reads as forecast
 * churn, so settling also invalidates the forecast calendar.
 */
export function useSettleChurn(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { qbId: number; settle?: SettleChurnRequest }) =>
      vars.settle
        ? apiFetch<ChurnPayload>(
            `/reconciliation/periods/${period}/churn/${vars.qbId}/settle`,
            { method: "POST", body: vars.settle },
          )
        : apiFetch<ChurnPayload>(
            `/reconciliation/periods/${period}/churn/${vars.qbId}/settle`,
            { method: "DELETE" },
          ),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "churn", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
      // Settled churn feeds the forecast calendar.
      void qc.invalidateQueries({ queryKey: ["financial-forecast"] });
    },
  });
}

// ── Admin: per-step user assignments ─────────────────────────────────────

export interface AssignedUser {
  user_id: string;
  name: string;
  email: string;
}

export interface StepAssignments {
  step_key: string;
  title: string;
  order: number;
  owner_roles: ReconRole[];
  users: AssignedUser[];
}

export interface AssignmentsPayload {
  steps: StepAssignments[];
  /** Active users for the picker. */
  users: AssignedUser[];
}

export function useStepAssignments(enabled = true) {
  return useQuery({
    queryKey: [KEY, "assignments"],
    queryFn: () =>
      apiFetch<AssignmentsPayload>("/reconciliation/admin/step-assignments"),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetStepAssignments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { stepKey: string; userIds: string[] }) =>
      apiFetch<AssignmentsPayload>(
        `/reconciliation/admin/step-assignments/${vars.stepKey}`,
        { method: "PUT", body: { user_ids: vars.userIds } },
      ),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "assignments"], data);
      // Assignments change can_act / visibility on every period view.
      void qc.invalidateQueries({ queryKey: [KEY, "period"] });
    },
  });
}

// ── month helpers ────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Current calendar month as "YYYY-MM". */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Day of the month from which the close defaults to the CURRENT month rather
 *  than the one just ended. */
export const CLOSE_MONTH_CUTOVER_DAY = 20;

/** The close month to land on by default.
 *
 *  Reconciliation is retrospective — you work a month after it has ended — so
 *  opening the workbench on the 12th and showing the half-finished current
 *  month is never what anyone wants. Before the 20th, default to the month
 *  just ended; from the 20th on, the current month is close enough to over to
 *  be the likely target. `recentMonths()` is unchanged, so the current month
 *  stays one click away in the picker either way.
 *
 *  Takes `now` so it's testable without faking the clock. */
export function defaultClosePeriod(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month1 = now.getMonth() + 1; // 1-based current month
  if (now.getDate() >= CLOSE_MONTH_CUTOVER_DAY) {
    return `${year}-${String(month1).padStart(2, "0")}`;
  }
  // Before the cutover → the month just ended, rolling back over January.
  return month1 === 1
    ? `${year - 1}-12`
    : `${year}-${String(month1 - 1).padStart(2, "0")}`;
}

/** "2026-05" → "May 2026". */
export function monthLabel(ym: string | null | undefined): string {
  if (!ym || ym.length < 7) return "—";
  const name = MONTH_NAMES[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(0, 4)}` : ym;
}

/** The current month and the prior `count - 1` months, newest first. */
export function recentMonths(count = 18): string[] {
  const out: string[] = [];
  const d = new Date();
  let y = d.getFullYear();
  let m = d.getMonth(); // 0-based
  for (let i = 0; i < count; i++) {
    out.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  return out;
}

// ── R-18 integration expense reconciliation ──────────────────────────────

/** How a vendor's invoice can be reconciled — a property of what it prints.
 *  `listed` vendors are recorded so the month's total is complete but are
 *  never matched to locations (MOTOR is a flat licence, AutoNetTV a content
 *  plugin on its own id space). */
export type VendorMode = "line" | "count" | "listed";

export type LineVerdict =
  | "cancellation_not_honoured"
  | "duplicate"
  | "unmapped"
  | "churned"
  | "cancelling"
  | "not_billing"
  | "no_data"
  | "count_delta"
  | "ok";

export type DispositionKind =
  | "cancel_requested"
  | "keep"
  | "already_cancelled"
  | "billing_correct";

export interface IntegrationVendor {
  id: string;
  slug: string;
  name: string;
  mode: VendorMode;
  entity_sources: string[];
  default_rate: number | null;
  unit_label: string;
  billing_basis: string;
  qb_vendor_names: string[];
  notes: string | null;
  is_active: boolean;
}

export interface IntegrationInvoice {
  id: string;
  vendor_id: string;
  vendor_name: string;
  vendor_mode: VendorMode;
  invoice_number: string;
  invoice_date: string | null;
  /** The month the invoice COVERS. Never inferred from invoice_date — vendors
   *  bill in arrears, in advance and quarterly. */
  service_period_start: string | null;
  service_period_end: string | null;
  subtotal: number;
  tax: number;
  total: number;
  invoiced_quantity: number | null;
  line_count: number;
  line_source: string;
  /** "rejected" = the parsed lines didn't sum to the header, so none were kept. */
  parse_status: string;
  parse_note: string | null;
  file_url: string | null;
  file_name: string | null;
}

export interface IntegrationLine {
  item_key: string;
  vendor_slug: string | null;
  vendor_name: string | null;
  invoice_number: string | null;
  vendor_location_ref: string | null;
  vendor_location_name: string | null;
  amount: number;
  verdict: LineVerdict | null;
  location_id: string | null;
  location_name: string | null;
  company_name: string | null;
  account_name?: string | null;
  txns_in_period: number;
  live_credentials?: number;
  company_cancel_date?: string | null;
  is_duplicate: boolean;
  disposition: string | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  // count-mode extras
  mode?: string;
  invoiced_quantity?: number;
  evidenced?: number;
  delta?: number;
  direction?: string;
  candidates?: IntegrationCandidate[];
  /** listed-mode context: is anyone actually using what we're licensing? */
  known_locations?: number;
  usage_note?: string;
  invoice_total?: number;
  /** count-mode: every Cinch location on the feed that produced data. The
   *  invoice names nobody, so this is the roster to reconcile against. */
  roster?: IntegrationCandidate[];
  roster_by_company?: {
    company_name: string;
    locations: number;
    /** Rooftop count equals the invoiced quantity — the strongest lead count
     *  mode can produce, and a one-click mapping candidate. */
    matches_invoiced_quantity: boolean;
  }[];
}

export interface IntegrationSummary {
  invoices: number;
  invoiced_total: number;
  lines: number;
  by_verdict: Record<string, number>;
  exception_lines: number;
  exception_amount: number;
  unmapped: number;
  flagged: number;
  pending: number;
  /** Counted in the month's spend, excluded from the exception math. */
  listed_total: number;
}

export interface IntegrationQbCost {
  qb_vendor_name: string;
  amount: number;
  document_count: number;
  sources: string | null;
  /** Catalog vendors this QuickBooks vendor pays. Empty = not in the catalog. */
  matched_vendors: string[];
  recorded_total: number;
  /** Not an error signal — QuickBooks posts on transaction date, invoices here
   *  are keyed to the service period they cover. */
  delta: number;
  /** Money left the building for a vendor this close has never heard of. */
  unrecorded: boolean;
}

export interface IntegrationNotInvoicedLocation {
  vendor_ref: string | null;
  location_id: string | null;
  location_name: string | null;
  company_name: string | null;
  city: string | null;
  state: string | null;
  txns_in_period: number;
}

/** Live on a vendor's feed, absent from every invoice line. The reverse of the
 *  worklist: not "should we pay this?" but "are we taking data we don't pay
 *  for?". `total` is the true count; `shown` is how many are listed. */
export interface IntegrationNotInvoiced {
  vendor_slug: string;
  vendor_name: string;
  total: number;
  shown: number;
  locations: IntegrationNotInvoicedLocation[];
}

export interface IntegrationExpensePayload {
  period: string;
  vendors: IntegrationVendor[];
  invoices: IntegrationInvoice[];
  lines: IntegrationLine[];
  unmapped: IntegrationLine[];
  qb_costs: IntegrationQbCost[];
  not_invoiced: IntegrationNotInvoiced[];
  summary: IntegrationSummary;
  matched_at: string | null;
  matched_by_name: string | null;
  can_act: boolean;
  locked: boolean;
  source: string;
}

export interface IntegrationCandidate {
  location_id: string | null;
  location_name: string | null;
  company_id?: string | null;
  company_name: string | null;
  city?: string | null;
  state?: string | null;
  store_number?: string | null;
  txns_in_period: number;
}

export interface IntegrationInvoiceCreate {
  vendor_id: string;
  invoice_number: string;
  invoice_date?: string | null;
  service_period_start?: string | null;
  service_period_end?: string | null;
  subtotal?: number;
  tax?: number;
  total?: number;
  invoiced_quantity?: number | null;
}

export function useIntegrationExpense(period: string) {
  return useQuery({
    queryKey: [KEY, "integration-expense", period],
    queryFn: () =>
      apiFetch<IntegrationExpensePayload>(
        `/reconciliation/periods/${period}/integration-expense`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

function useInvalidateIntegration(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "integration-expense", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

export function useCreateIntegrationInvoice(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (body: IntegrationInvoiceCreate) =>
      apiFetch<IntegrationInvoice>(
        `/reconciliation/periods/${period}/integration-expense/invoices`,
        { method: "POST", body },
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteIntegrationInvoice(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (invoiceId: string) =>
      apiFetch<void>(
        `/reconciliation/periods/${period}/integration-expense/invoices/${invoiceId}`,
        { method: "DELETE" },
      ),
    onSuccess: invalidate,
  });
}

/** Import per-location line detail (.xlsx / .csv). The server rejects the whole
 *  parse if the lines don't sum to the invoice header. */
export function useImportIntegrationLines(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (vars: { invoiceId: string; file: File }) => {
      const form = new FormData();
      form.append("file", vars.file);
      return apiFetch<IntegrationInvoice>(
        `/reconciliation/periods/${period}/integration-expense/invoices/${vars.invoiceId}/lines`,
        { method: "POST", body: form },
      );
    },
    onSuccess: invalidate,
  });
}

export interface IntegrationMatchResult {
  invoices: number;
  matched: number;
  unmapped: number;
  exceptions: number;
  not_invoiced: number;
}

/** R-18's pull: deterministic ENTITY_REF match + confirmed mappings, then the
 *  verdicts freeze. Anything unmatched stays `unmapped` for the Mapping tab. */
export function useMatchIntegrationExpense(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: () =>
      apiFetch<IntegrationMatchResult>(
        `/reconciliation/periods/${period}/integration-expense/match`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });
}

export function useReviewIntegrationLine(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (vars: {
      item_key: string;
      review_status: ReviewStatus;
      review_notes?: string | null;
    }) =>
      apiFetch<IntegrationLine>(
        `/reconciliation/periods/${period}/integration-expense/review`,
        { method: "PATCH", body: vars },
      ),
    onSuccess: invalidate,
  });
}

export interface IntegrationBulkReviewResult {
  updated: number;
  requested: number;
}

/** Apply one verdict to an explicit set of lines — the visible, filtered set.
 *  Sends the exact keys rather than a filter, so the server can never act on
 *  rows the user couldn't see. */
export function useBulkReviewIntegrationLines(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (vars: {
      item_keys: string[];
      review_status: ReviewStatus;
      review_notes?: string | null;
    }) =>
      apiFetch<IntegrationBulkReviewResult>(
        `/reconciliation/periods/${period}/integration-expense/review-bulk`,
        { method: "PATCH", body: vars },
      ),
    onSuccess: invalidate,
  });
}

export function useIntegrationCandidates(
  period: string,
  vendorId: string | null,
  query: string,
) {
  return useQuery({
    queryKey: [KEY, "integration-candidates", period, vendorId, query],
    queryFn: () =>
      apiFetch<IntegrationCandidate[]>(
        `/reconciliation/periods/${period}/integration-expense/candidates` +
          `?vendor_id=${encodeURIComponent(vendorId ?? "")}&q=${encodeURIComponent(query)}`,
      ),
    enabled: !!period && !!vendorId,
    staleTime: 60_000,
  });
}

/** Promote a vendor-ref → Cinch-location mapping to durable truth. The only
 *  path that clears an `unmapped` verdict. */
export function useConfirmIntegrationMapping(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (body: {
      vendor_id: string;
      vendor_location_ref: string;
      vendor_location_name?: string | null;
      location_id?: string | null;
      location_name?: string | null;
      company_id?: string | null;
      company_name?: string | null;
      note?: string | null;
    }) =>
      apiFetch<unknown>(
        `/reconciliation/periods/${period}/integration-expense/mappings`,
        { method: "POST", body },
      ),
    onSuccess: invalidate,
  });
}

/** The follow-through ledger: what we decided to DO about a line. */
export function useSetIntegrationDisposition(period: string) {
  const invalidate = useInvalidateIntegration(period);
  return useMutation({
    mutationFn: (body: {
      vendor_id: string;
      vendor_location_ref: string;
      vendor_location_name?: string | null;
      disposition: DispositionKind;
      reason?: string | null;
      expected_off_invoice_month?: string | null;
      monthly_amount?: number;
    }) =>
      apiFetch<unknown>(
        `/reconciliation/periods/${period}/integration-expense/dispositions`,
        { method: "POST", body },
      ),
    onSuccess: invalidate,
  });
}

/** Verdict → label + tone. Exceptions are money we should probably not pay. */
export const VERDICT_META: Record<
  string,
  { label: string; tone: "success" | "warning" | "danger" | "neutral"; hint: string }
> = {
  ok: { label: "OK", tone: "success", hint: "Mapped, data flowed, customer still billing" },
  no_data: {
    label: "No data",
    tone: "warning",
    hint: "Mapped, but zero transactions in the service period",
  },
  churned: {
    label: "Churned",
    tone: "danger",
    hint: "The account this location rolls up to has left — we should not be paying for this feed",
  },
  cancelling: {
    label: "Cancelling",
    tone: "warning",
    hint: "Notice is on record but billing hasn't ended — cancel the vendor connection before it does",
  },
  not_billing: {
    label: "Not billing",
    tone: "danger",
    hint: "Account absent from this month's billed actuals",
  },
  unmapped: {
    label: "Unmapped",
    tone: "warning",
    hint: "No confirmed Cinch location for this invoice line",
  },
  duplicate: {
    label: "Duplicate",
    tone: "danger",
    hint: "The vendor billed this location more than once on one invoice",
  },
  cancellation_not_honoured: {
    label: "Not honoured",
    tone: "danger",
    hint: "We asked the vendor to cancel this and it is still being billed",
  },
  count_delta: {
    label: "Count delta",
    tone: "warning",
    hint: "The invoiced quantity does not match the connections we can evidence",
  },
};


/* ── R-17 · companies to internal accounts verified ──────────────────────── */

export interface InternalCompany {
  item_key: string;
  account_id: string | null;
  account_name: string | null;
  account_qb_id: number | null;
  /** Budget the usage is charged to, copied from the roster at pull time. */
  department: string | null;
  company_id: string | null;
  company_name: string | null;
  company_created: string | null;
  company_active: boolean;
  locations: number;
  contacts: number;
  /** Human-legible message count; `text_segments` is what carriers bill. */
  text_messages: number;
  text_segments: number;
  /** Always null today — the department charge-back isn't computed anywhere
   *  yet. The column ships blank so it already exists when billing can fill it. */
  amount_billed: number | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface InternalCandidate {
  item_key: string;
  account_id: string | null;
  account_name: string | null;
  account_qb_id: number | null;
  account_type: string | null;
  company_id: string | null;
  company_name: string | null;
  company_created: string | null;
  company_active: boolean;
  contacts: number;
  text_messages: number;
  text_segments: number;
  users_all: number;
  users_cinch: number;
  /** Which checks fired: "name" and/or "cinch_users". */
  signals: string[];
  /** Sent messages this month — a live cost question rather than cleanup. */
  has_usage: boolean;
  amount_billed: number | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface InternalAccountRow {
  id: string;
  cinch_account_id: string;
  quickbooks_id: number | null;
  name: string;
  department: string | null;
  is_active: boolean;
  notes: string | null;
  company_count: number;
  contacts: number;
  text_messages: number;
  text_segments: number;
  pending_count: number;
  flagged_count: number;
}

export interface InternalAccountsSummary {
  company_count: number;
  contacts: number;
  text_messages: number;
  text_segments: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
  candidate_count: number;
  candidate_with_usage: number;
  candidate_pending: number;
  candidate_flagged: number;
}

export interface InternalAccountsPayload {
  period: string;
  pulled_at: string | null;
  pulled_by_name: string | null;
  can_act: boolean;
  locked: boolean;
  /** False when the last pull fell back to matching candidates on name alone. */
  users_signal_ok: boolean;
  summary: InternalAccountsSummary;
  accounts: InternalAccountRow[];
  companies: InternalCompany[];
  candidates: InternalCandidate[];
}

export interface InternalCandidateDismissal {
  cinch_company_id: string;
  company_name: string | null;
  account_name: string | null;
  reason: string | null;
  dismissed_in_period: string | null;
  dismissed_by_name: string | null;
  created_at: string | null;
}

/** Why a candidate is on the list. The users signal is the noisier of the two
 *  on its own — plenty of paying customers have only a Cinch CSM attached — so
 *  the UI names which fired rather than showing a single opaque score. */
export const SIGNAL_META: Record<string, { label: string; hint: string }> = {
  name: {
    label: "name",
    hint: "The company name matches the internal vocabulary (cinch, demo, test, sandbox, training, internal, dummy, sample).",
  },
  cinch_users: {
    label: "cinch users",
    hint: "Every non-superuser on this company has an @cinch.io address. Noisy on its own — a customer whose only user is their CSM looks the same.",
  },
};

export function useInternalAccounts(period: string) {
  return useQuery({
    queryKey: [KEY, "internal-accounts", period],
    queryFn: () =>
      apiFetch<InternalAccountsPayload>(
        `/reconciliation/periods/${period}/internal-accounts`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

function useInvalidateInternal(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "internal-accounts", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

export function usePullInternalAccounts(period: string) {
  const invalidate = useInvalidateInternal(period);
  return useMutation({
    mutationFn: () =>
      apiFetch<InternalAccountsPayload>(
        `/reconciliation/periods/${period}/internal-accounts/pull`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });
}

export function useReviewInternalItem(period: string) {
  const invalidate = useInvalidateInternal(period);
  return useMutation({
    mutationFn: (vars: {
      item_key: string;
      review_status: ReviewStatus;
      review_notes?: string | null;
    }) =>
      apiFetch<InternalCompany>(
        `/reconciliation/periods/${period}/internal-accounts/review`,
        { method: "PATCH", body: vars },
      ),
    onSuccess: invalidate,
  });
}

export interface InternalBulkReviewResult {
  updated: number;
  requested: number;
}

/** Apply one verdict to an explicit set of rows — the visible, filtered set.
 *  Sends the exact keys rather than a filter, so the server can never act on
 *  rows the user never saw. */
export function useBulkReviewInternalItems(period: string) {
  const invalidate = useInvalidateInternal(period);
  return useMutation({
    mutationFn: (vars: {
      item_keys: string[];
      review_status: ReviewStatus;
      review_notes?: string | null;
    }) =>
      apiFetch<InternalBulkReviewResult>(
        `/reconciliation/periods/${period}/internal-accounts/review-bulk`,
        { method: "PATCH", body: vars },
      ),
    onSuccess: invalidate,
  });
}

/** Rule a candidate NOT internal, durably — it stays off future months' lists
 *  unless it starts sending, which re-raises it regardless. */
export function useDismissInternalCandidate(period: string) {
  const invalidate = useInvalidateInternal(period);
  return useMutation({
    mutationFn: (vars: {
      cinch_company_id: string;
      company_name?: string | null;
      account_name?: string | null;
      reason?: string | null;
    }) =>
      apiFetch<InternalCandidateDismissal>(
        `/reconciliation/periods/${period}/internal-accounts/dismiss`,
        { method: "POST", body: vars },
      ),
    onSuccess: invalidate,
  });
}

export function useUpsertInternalAccount(period: string) {
  const invalidate = useInvalidateInternal(period);
  return useMutation({
    mutationFn: (vars: {
      cinch_account_id: string;
      name?: string | null;
      quickbooks_id?: number | null;
      department?: string | null;
      notes?: string | null;
      is_active?: boolean | null;
    }) =>
      apiFetch<InternalAccountRow>(`/reconciliation/internal-accounts/roster`, {
        method: "PUT",
        body: vars,
      }),
    onSuccess: invalidate,
  });
}

// ── R-15 general billing data validation ─────────────────────────────────

/** One structural rule, described for whoever has to fix it. */
export interface BillingValidationCheck {
  key: string;
  title: string;
  /** Tab label — ten full titles don't fit across a tab strip. */
  short_title: string;
  /** 'company' | 'account' | 'contract' */
  entity: string;
  why: string;
  fix: string;
  /** 'danger' = actively costing or hiding money; 'warning' = structural. */
  severity: string;
}

/** One record that breaks one check, plus its verdict. */
export interface BillingValidationItem {
  item_key: string;
  check_key: string;
  check_title: string;
  entity: string;
  severity: string;
  entity_id: string;
  entity_name: string | null;
  related_name: string | null;
  /** Only meaningful on account_no_contract: this account is taking money. */
  is_billing: boolean;
  billed: number;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface BillingValidationSummary {
  exception_count: number;
  billing_without_contract_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
  counts_by_check: Record<string, number>;
}

export interface BillingValidationPayload {
  period: string;
  can_act: boolean;
  locked: boolean;
  frozen_at: string | null;
  summary: BillingValidationSummary | null;
  checks: BillingValidationCheck[];
  items: BillingValidationItem[];
}

export function useBillingValidation(period: string) {
  return useQuery({
    queryKey: [KEY, "billing-validation", period],
    queryFn: () =>
      apiFetch<BillingValidationPayload>(
        `/reconciliation/periods/${period}/billing-validation`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

function useInvalidateBillingValidation(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "billing-validation", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

export function useReviewBillingValidation(period: string) {
  const invalidate = useInvalidateBillingValidation(period);
  return useMutation({
    // item_key goes in the query string because it contains a colon.
    mutationFn: (vars: { itemKey: string; status: ReviewStatus; notes?: string | null }) =>
      apiFetch<BillingValidationItem>(
        `/reconciliation/periods/${period}/billing-validation/review?item_key=${encodeURIComponent(vars.itemKey)}`,
        { method: "PATCH", body: { status: vars.status, notes: vars.notes } },
      ),
    onSuccess: invalidate,
  });
}

export function useBulkReviewBillingValidation(period: string) {
  const invalidate = useInvalidateBillingValidation(period);
  return useMutation({
    mutationFn: (vars: { itemKeys: string[]; status: ReviewStatus }) =>
      apiFetch<{ updated: number; requested: number }>(
        `/reconciliation/periods/${period}/billing-validation/review-bulk`,
        { method: "PATCH", body: { item_keys: vars.itemKeys, status: vars.status } },
      ),
    onSuccess: invalidate,
  });
}

/** The full Cinch record behind one exception. `record` is Cinch's detail
 *  serializer passed through, so its shape depends on `entity` — the drawer
 *  narrows it per type rather than pretending one interface fits all three. */
export interface BillingValidationDetail {
  item_key: string;
  entity: string;
  entity_id: string;
  record: Record<string, unknown> | null;
  error: string | null;
}

/** Only fetched once a row is opened — one Cinch call per row would be absurd
 *  on a 257-row worklist. */
export function useBillingValidationDetail(period: string, itemKey: string | null) {
  return useQuery({
    queryKey: [KEY, "billing-validation-detail", itemKey],
    queryFn: () =>
      apiFetch<BillingValidationDetail>(
        `/reconciliation/periods/${period}/billing-validation/detail?item_key=${encodeURIComponent(itemKey ?? "")}`,
      ),
    enabled: !!period && !!itemKey,
    staleTime: 30_000,
  });
}

// ── R-16 review parent/child company relationships ───────────────────────

/** What happened to a relationship change inside the close month. */
export type ParentChildStageKey =
  | "requested"
  | "approved"
  | "verified"
  | "rejected"
  | "cancelled";

/** One stage, as a tab label. Sent by the API so the tabs can't drift from
 *  the stages the rows actually carry. */
export interface ParentChildStage {
  key: ParentChildStageKey;
  label: string;
}

/** One workflow event on a change, inside the close month. */
export interface ParentChildEvent {
  /** request_created | approved | applied_verified | rejected | cancelled */
  event: string;
  at: string | null;
  actor_name: string | null;
  /** The requester's reason or the reviewer's note, whichever it carries. */
  detail: string | null;
}

/** One parent/child relationship change that moved in the close month.
 *
 *  `review_status` is the shared worklist vocabulary — `approved` is presented
 *  as **Verified** here, because the reviewer is confirming the child really
 *  belongs under the parent, not re-approving the request. */
export interface ParentChildItem {
  item_key: string;
  /** Null for a trail row whose request was cleaned up. */
  request_id: string | null;
  /** add | remove | move */
  action: string;
  action_label: string;
  parent_company_id: string;
  parent_name: string | null;
  child_company_id: string;
  child_name: string | null;
  /** move only: where the child ended up. */
  new_parent_company_id: string | null;
  new_parent_name: string | null;
  stage: ParentChildStageKey;
  stage_label: string;
  reason: string | null;
  /** The request's CURRENT state, which can be past the month's stage. */
  current_status: string | null;
  requested_at: string | null;
  requested_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  verified_at: string | null;
  applied_at: string | null;
  apply_error: string | null;
  /** Approved but not yet confirmed live in Cinch: decided, tree unchanged. */
  awaiting_apply: boolean;
  /** Latest AI verdict on the parent group ('clear' | 'flagged') — context. */
  ai_status: string | null;
  ai_summary: string | null;
  events: ParentChildEvent[];
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface ParentChildSummary {
  change_count: number;
  requested_count: number;
  approved_count: number;
  verified_count: number;
  /** Approved changes Cinch hasn't confirmed — the tree is still the old shape. */
  awaiting_apply_count: number;
  ai_flagged_count: number;
  pending_review_count: number;
  reviewed_count: number;
  flagged_count: number;
  counts_by_stage: Record<string, number>;
}

export interface ParentChildPayload {
  period: string;
  can_act: boolean;
  locked: boolean;
  frozen_at: string | null;
  summary: ParentChildSummary | null;
  stages: ParentChildStage[];
  items: ParentChildItem[];
}

export function useParentChild(period: string) {
  return useQuery({
    queryKey: [KEY, "parent-child", period],
    queryFn: () =>
      apiFetch<ParentChildPayload>(
        `/reconciliation/periods/${period}/parent-child`,
      ),
    enabled: !!period,
    staleTime: 60_000,
  });
}

function useInvalidateParentChild(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "parent-child", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

export function useReviewParentChild(period: string) {
  const invalidate = useInvalidateParentChild(period);
  return useMutation({
    // item_key goes in the query string because it contains a colon.
    mutationFn: (vars: { itemKey: string; status: ReviewStatus; notes?: string | null }) =>
      apiFetch<ParentChildItem>(
        `/reconciliation/periods/${period}/parent-child/review?item_key=${encodeURIComponent(vars.itemKey)}`,
        { method: "PATCH", body: { status: vars.status, notes: vars.notes } },
      ),
    onSuccess: invalidate,
  });
}

export function useBulkReviewParentChild(period: string) {
  const invalidate = useInvalidateParentChild(period);
  return useMutation({
    mutationFn: (vars: { itemKeys: string[]; status: ReviewStatus }) =>
      apiFetch<{ updated: number; requested: number }>(
        `/reconciliation/periods/${period}/parent-child/review-bulk`,
        { method: "PATCH", body: { item_keys: vars.itemKeys, status: vars.status } },
      ),
    onSuccess: invalidate,
  });
}

// ── R-08 replacement supersession ────────────────────────────────────────

/** Whether the contract being replaced was properly closed at the handoff.
 *  `ok` is the norm — Cinch stamps it when the replacement is created — so
 *  anything else is the finding. `unverifiable` means there's no replaced
 *  contract linked to check against. */
export type SuppressionVerdict =
  | "ok"
  | "replaced_still_open"
  | "amendment_still_open"
  | "handoff_gap"
  | "handoff_overlap"
  | "unverifiable";

/** Where the baseline came from. Only `prior_billed` is produced now — the
 *  baseline is what the account was billed the period before signing. `none`
 *  means there was no billing to compare against, so the row is unvalued. */
export type BaselineSource = "replaced_stack" | "prior_billed" | "none";

/** One replacement, valued against what the account already had.
 *
 *  Every money field is nullable on purpose. `net_change_mrr === null` means
 *  "we can't say" — a reseller instrument with no meaningful baseline, or no
 *  baseline known at all — which is a different finding from a net of 0. */
export interface ReplacementRow {
  contract_id: string;
  company_name: string | null;
  company_dba: string | null;
  account_name: string | null;
  qb_id: number | null;
  contract_type_name: string | null;
  sign_date: string | null;
  billing_effective_from: string | null;
  term_months: number | null;
  closer_name: string | null;
  pandadoc_id: string | null;
  hubspot_deal_id: string | null;

  replacement_mrr: number;
  replacement_arr: number;
  replaced_contract_id: string | null;
  replaced_company_name: string | null;
  replaced_type: string | null;
  replaced_mrr: number | null;
  amendment_mrr: number;
  amendment_count: number;
  amendment_open_count: number;
  replaced_stack_mrr: number | null;
  replaced_stack_arr: number | null;
  /** null = no waterfall row at all, i.e. unknown — not "$0 billed". */
  prior_billed_mrr: number | null;
  prior_billed_arr: number | null;
  prior_period: string | null;
  baseline_mrr: number | null;
  baseline_arr: number | null;
  baseline_source: BaselineSource;
  net_change_mrr: number | null;
  net_change_arr: number | null;
  valued: boolean;

  suppression: SuppressionVerdict;
  /** Days between the replaced contract ending and this one starting; 1 is clean. */
  handoff_gap_days: number | null;
  replaced_eff_from: string | null;
  replaced_eff_through: string | null;
  replaced_canceled_at: string | null;

  flags: string[];
  other_open_count: number;
  other_open_mrr: number;
  /** false = carried in from an earlier month because its suppression is still
   *  broken. Those don't count toward this month's net bookings. */
  in_period: boolean;

  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_superseded: boolean;

  /** The durable record that settles this replacement. Its absence is what
   *  keeps the row on the worklist. */
  verification: ReplacementVerification | null;
  /** Still needs verifying — no record, or one that no longer describes it.
   *  The field the whole worklist is built on. */
  is_open: boolean;
}

/** What retires a replacement: `verified` = link and net settled;
 *  `no_replacement` = confirmed to supersede nothing (a data-quality find). */
export type VerificationStatus = "verified" | "no_replacement";

export interface ReplacementVerification {
  status: VerificationStatus;
  replaced_contract_id: string | null;
  replaced_mrr: number | null;
  baseline_mrr: number | null;
  net_change_mrr: number | null;
  net_change_arr: number | null;
  net_override: boolean;
  override_reason: string | null;
  notes: string | null;
  verified_in_period: string | null;
  verified_by_name: string | null;
  verified_at: string | null;
  /** The contract changed after this was settled, so the row reopens. */
  stale: boolean;
}

export interface ReplacementCandidate {
  contract_id: string;
  company_name: string | null;
  company_dba: string | null;
  contract_type_name: string | null;
  mrr_total: number | null;
  sign_date: string | null;
  billing_effective_from: string | null;
  billing_effective_through: string | null;
  days_before_handoff: number | null;
  name_match: number;
  was_open_at_handoff: boolean;
  confidence: "high" | "medium" | "low";
}

export interface ReplacementCandidates {
  contract_id: string;
  contract_type_name: string | null;
  allowed_types: string[];
  candidates: ReplacementCandidate[];
}

/** What Cinch says right now, for a row whose Snowflake copy can be a day
 *  behind. `drift` names what the mart hasn't caught up on. */
export interface ReplacementLiveState {
  available: boolean;
  replaced_contract_id: string | null;
  replaced_company_name: string | null;
  replaced_eff_through: string | null;
  suppression: SuppressionVerdict | null;
  drift: string[];
  error: string | null;
}

export interface ReplacementSummary {
  // The standing backlog.
  total_count: number;
  open_count: number;
  verified_count: number;
  no_replacement_count: number;
  stale_verification_count: number;
  missing_link_count: number;
  // This close month.
  replacement_count: number;
  open_in_month_count: number;
  net_change_arr: number;
  unvalued_count: number;

  suppression_failure_count: number;
  unverifiable_count: number;
  pending_count: number;
  approved_count: number;
  flagged_count: number;
}

export interface ReplacementPayload {
  period: string;
  can_act: boolean;
  locked: boolean;
  frozen_at: string | null;
  summary: ReplacementSummary | null;
  replacements: ReplacementRow[];
  source: string;
  error: string | null;
}

export interface ReplacementHistoryMonth {
  period: string;
  subscription: number;
  usage_other: number;
  total_billed: number;
}

/** The drawer's revenue-impact comparison: the twelve months before signing,
 *  actual, against what the replacement would have billed over the same window.
 *  Two readings — the contracted commitment alone, and all-in with usage
 *  continuing on top at its recent run rate. */
export interface ReplacementHistory {
  months: ReplacementHistoryMonth[];
  months_observed: number;
  actual_subscription: number;
  actual_usage: number;
  actual_all_in: number;
  new_subscription: number;
  subscription_change: number;
  projected_usage_monthly: number;
  projected_usage: number;
  projected_all_in: number;
  all_in_change: number;
  usage_projection_months: number;
}

/** Human labels for the row-level trust flags — why a net might not mean what
 *  it looks like. Kept beside the type so a new backend flag shows up as a raw
 *  key rather than silently vanishing from the UI. */
export const REPLACEMENT_FLAG_LABELS: Record<string, string> = {
  no_replaced_link: "No replaced contract linked",
  no_baseline_computable: "Reseller instrument — no baseline",
  baseline_unknown: "No billing history for the prior month",
  no_baseline_at_all: "Nothing to measure against",
  mixed_account_baseline: "Account carries other open contracts",
  future_handoff: "Takes effect after this month",
  no_effective_date: "No billing effective date",
};

export const SUPPRESSION_LABELS: Record<SuppressionVerdict, string> = {
  ok: "Closed cleanly",
  replaced_still_open: "Replaced contract still open",
  amendment_still_open: "Upsell still open",
  handoff_gap: "Gap at handoff",
  handoff_overlap: "Overlap at handoff",
  unverifiable: "Can't verify",
};

export function useReplacements(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "replacements", period],
    queryFn: () =>
      apiFetch<ReplacementPayload>(`/reconciliation/periods/${period}/replacements`),
    enabled: enabled && !!period,
    staleTime: 60_000,
  });
}

export function useReviewReplacement(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      contractId: string;
      status: ReviewStatus;
      notes?: string | null;
    }) =>
      apiFetch<ReplacementRow>(
        `/reconciliation/periods/${period}/replacements/${vars.contractId}/review`,
        { method: "PATCH", body: { status: vars.status, notes: vars.notes } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "replacements", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
    },
  });
}

/** Fetched per-replacement when the drawer opens — twelve months of billing is
 *  a real query, and most rows are never opened. */
export function useReplacementHistory(period: string, contractId: string | null) {
  return useQuery({
    queryKey: [KEY, "replacement-history", period, contractId],
    queryFn: () =>
      apiFetch<ReplacementHistory>(
        `/reconciliation/periods/${period}/replacements/${contractId}/history`,
      ),
    enabled: !!period && !!contractId,
    staleTime: 300_000,
  });
}

/** The replacement's own contract record, live from Cinch — line items,
 *  discounts, lineage. Same payload as R-14's drawer. */
export function useReplacementContract(period: string, contractId: string | null) {
  return useQuery({
    queryKey: [KEY, "replacement-contract", period, contractId],
    queryFn: () =>
      apiFetch<SignedContractDetail>(
        `/reconciliation/periods/${period}/replacements/${contractId}/contract`,
      ),
    enabled: !!period && !!contractId,
    staleTime: 300_000,
  });
}

function useInvalidateReplacements(period: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY, "replacements", period] });
    void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  };
}

/** Settle what a replacement superseded and what it netted — the durable record
 *  that takes it off the backlog for good, in every future month. */
export function useVerifyReplacement(period: string) {
  const invalidate = useInvalidateReplacements(period);
  return useMutation({
    mutationFn: (vars: {
      contractId: string;
      status: VerificationStatus;
      net_change_mrr?: number | null;
      override_reason?: string | null;
      notes?: string | null;
    }) =>
      apiFetch<ReplacementRow>(
        `/reconciliation/periods/${period}/replacements/${vars.contractId}/verification`,
        {
          method: "PUT",
          body: {
            status: vars.status,
            net_change_mrr: vars.net_change_mrr,
            override_reason: vars.override_reason,
            notes: vars.notes,
          },
        },
      ),
    onSuccess: invalidate,
  });
}

/** Reopen a settled replacement — back onto the worklist. */
export function useUnverifyReplacement(period: string) {
  const invalidate = useInvalidateReplacements(period);
  return useMutation({
    mutationFn: (contractId: string) =>
      apiFetch<void>(
        `/reconciliation/periods/${period}/replacements/${contractId}/verification`,
        { method: "DELETE" },
      ),
    onSuccess: invalidate,
  });
}

/** Contracts this replacement could have superseded. Fetched only when the
 *  drawer needs them — i.e. when nothing is linked yet. */
export function useReplacementCandidates(period: string, contractId: string | null) {
  return useQuery({
    queryKey: [KEY, "replacement-candidates", period, contractId],
    queryFn: () =>
      apiFetch<ReplacementCandidates>(
        `/reconciliation/periods/${period}/replacements/${contractId}/candidates`,
      ),
    enabled: !!period && !!contractId,
    staleTime: 300_000,
  });
}

/** What Cinch says right now. Snowflake mirrors nightly, which is longer than a
 *  close will wait to see its own work, so this is the "did my change land?"
 *  check. Not fetched automatically — it costs a live Cinch call per row. */
export function useReplacementLiveState(
  period: string,
  contractId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [KEY, "replacement-live", period, contractId],
    queryFn: () =>
      apiFetch<ReplacementLiveState>(
        `/reconciliation/periods/${period}/replacements/${contractId}/live`,
      ),
    enabled: enabled && !!period && !!contractId,
    staleTime: 0,
  });
}

/** Point a replacement at what it superseded — **a write to Cinch**, not to us,
 *  because every other consumer reads the link from there. Needs billing access
 *  (Cinch gates billing writes to superuser), so a close reviewer without it
 *  gets a 403 and a superuser applies it for them. */
export function useSetReplacedContract(period: string) {
  const invalidate = useInvalidateReplacements(period);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contractId: string; replacedContractId: string }) =>
      apiFetch<unknown>(
        `/reconciliation/periods/${period}/replacements/${vars.contractId}/replaced-contract`,
        { method: "PUT", body: { replaced_contract_id: vars.replacedContractId } },
      ),
    onSuccess: (_d, vars) => {
      invalidate();
      // The mart won't have this for hours; the live check is how the reviewer
      // sees it landed.
      void qc.invalidateQueries({
        queryKey: [KEY, "replacement-live", period, vars.contractId],
      });
    },
  });
}

/** One billable item across the three columns of the line-item comparison. */
export interface ReplacementLine {
  item_name: string;
  is_subscription: boolean;
  replaced_mrr: number | null;
  billed_prior: number | null;
  billed_avg: number | null;
  /** What this row contributes to the baseline: prior period for a subscription
   *  line, the rolling average for a usage line. */
  baseline_mrr: number | null;
  replacement_mrr: number | null;
  change_mrr: number;
  usage_months: number | null;
  /** The synthesized base-fee row — base_fee isn't a line item but bills as one. */
  is_base_fee: boolean;
  /** Contract value Cinch holds no line items for. */
  is_remainder: boolean;
}

/** The replaced contract, the billing just before it, and the replacement — item
 *  by item, so the headline net can be read down a column. The `*_total` fields
 *  tie to the headline; subscription and usage are subtotalled apart because only
 *  subscription is in the baseline. */
export interface ReplacementLineComparison {
  lines: ReplacementLine[];
  usage_months: number;
  prior_period: string | null;
  replaced_total: number | null;
  baseline_total: number;
  usage_total: number;
  replacement_total: number;
  change_total: number;
  headline_baseline: number | null;
  headline_net_change: number | null;
  contract_replacement_mrr: number;
  contract_replaced_mrr: number | null;
  /** False when Cinch holds no line items — the billed column still works. */
  has_contract_lines: boolean;
}

export function useReplacementLineItems(period: string, contractId: string | null) {
  return useQuery({
    queryKey: [KEY, "replacement-lines", period, contractId],
    queryFn: () =>
      apiFetch<ReplacementLineComparison>(
        `/reconciliation/periods/${period}/replacements/${contractId}/line-items`,
      ),
    enabled: !!period && !!contractId,
    staleTime: 300_000,
  });
}
